import { initializeApp } from "@firebase/app";
import { getMessaging, onMessage, getToken } from "@firebase/messaging";
import merge from "deepmerge";
import Device from "./Device";
import StorageService from "./StorageService";
import PushApiClient from "./PushApiClient";
import defaultConfig from "./default-config";
import { defaultTagsFilter, absoluteUrl } from "./helpers";

export default class PushDelivery {
  constructor(config) {
    this.config = merge(defaultConfig, config);
    this.validateConfig(config);
    this.storageService = new StorageService(this.config);
    this.pushApiClient = new PushApiClient(config);
    this.firebaseApp;

    if (true === config.globalScope) {
      window.pushDelivery = this;
    }
  }

  setPayloadHandler(handler) {
    this.handlePayload = handler;
  }

  async init() {
    await this.storageService.init();
    this.initFirebase();
    this.setMessagingHandlers();

    const permGranted = Notification.permission === "granted";
    if (permGranted) {
      const res = await this.initServiceWorker();
      if (!this.getDevice().getId()) {
        await this.retrieveAndStoreDeviceToken(res);
        console.log("device token retrieved", this.getDevice().getId());
        await this.subscribeToDefaultTags();
      }
    } else if (!permGranted && this.getDevice().getId()) {
      const subscribedTags = await this.getSubscribedTags();
      await this.unsubscribeFrom(subscribedTags);
      this.storageService.removeItemLocalStorage(this.config.app.localStorage.deviceStorageKey);
    }
  }

  initFirebase() {
    this.firebaseApp = initializeApp({
      messagingSenderId: this.config.app.firebase.messageSenderId,
      projectId: this.config.app.firebase.projectId,
      apiKey: this.config.app.firebase.apiKey,
      appId: this.config.app.firebase.appId,
    });
    this.messaging = getMessaging(this.firebaseApp);
  }

  initServiceWorker() {
    return new Promise(async (resolve, reject) => {
      const swConfigParam = `?cfg=${encodeURIComponent(JSON.stringify(this.config.app))}`;
      const swUrl = this.config.app.firebase.serviceWorkerPath || absoluteUrl("service-worker.js");
      console.log("service worker url", swUrl);
      navigator.serviceWorker
        .register(swUrl + swConfigParam, { type: "module" })
        .then(async (reg) => {
          console.log("service worker registered", reg.scope);
          resolve(reg);
        })
        .catch((e) => {
          console.log("failed to register service worker", e);
          reject(e);
        });
    });
  }

  setMessagingHandlers() {
    const pushDelivery = this;
    onMessage(this.messaging, (payload) => {
      let notificationTitle = payload.data.title;
      let url = pushDelivery.config.app.client.defaultUrl;
      console.log(payload);
      this.resubscribeCheck();
      try {
        if (payload.data.url) {
          const urlObj = new URL(payload.data.url);
          urlObj.searchParams.append("platform", "desktop");
          if (payload.data.articleID) {
            urlObj.searchParams.append("cmsId", payload.data.articleID);
          }
          if (payload.data.id) {
            urlObj.searchParams.append("id", payload.data.id);
          }
          url = urlObj.href;
        }
        if (!payload.data.title) {
          notificationTitle = " ";
        }

        if (this.handlePayload) {
          this.handlePayload(payload);
        }

        const note = {
          date: new Date(Date.now()),
          text: payload.data.message || payload.data.body,
          url: url,
          title: notificationTitle,
          ...payload,
        };
        pushDelivery.storageService.save(note);
      } catch (e) {
        console.log("failed", e);
      }
    });
  }

  retrieveAndStoreDeviceToken(registration) {
    return new Promise(async (resolve, reject) => {
      getToken(this.messaging, { serviceWorkerRegistration: registration })
        .then((token) => {
          const device = new Device();
          device.id = token;
          this.storageService.storeItemLocalStorage(this.config.app.localStorage.deviceStorageKey, device);
          this.storageService.storeItemLocalStorage(this.config.app.localStorage.lastTokenUpdateKey, new Date());
          this.storeHeartbeat();
          resolve(token);
        })
        .catch((e) => {
          console.log("failed!", e);
          reject(e);
        });
    });
  }

  getDevice() {
    const storedData = this.storageService.getItemLocalStorage(this.config.app.localStorage.deviceStorageKey);
    if (storedData) {
      return new Device(storedData.id);
    } else {
      return new Device(null);
    }
  }

  async subscribeToDefaultTags() {
    const allTags = await this.getTags();
    return this.getDefaultSubscriptionTags().then(
      async (tagIdsToSubscribe) => {
        await this.subscribeTo(tagIdsToSubscribe);
        this.storeHeartbeat();
        const defaultSubscribedTags = allTags[0].tags.filter((tag) => tagIdsToSubscribe.includes(tag.id));
        this.storageService.addCache("subscribedTags", defaultSubscribedTags, 3600);
        return tagIdsToSubscribe.map((id) => {
          return { "id": id };
        });
      },
      (e) => {
        console.log("Error on defaultSubscribe:", e);
      }
    );
  }

  getDefaultSubscriptionTags() {
    if (this.config.app.client.defaultSubscribedTags) {
      if (typeof this.config.app.client.defaultSubscribedTags === "function") {
        return this.getTags().then((tags) => {
          return this.config.app.client.defaultSubscribedTags(tags);
        });
      } else if (Array.isArray(this.config.app.client.defaultSubscribedTags)) {
        return Promise.resolve(this.config.app.client.defaultSubscribedTags);
      } else if (this.config.app.client.defaultSubscribedTags) {
        return Promise.resolve([this.config.app.client.defaultSubscribedTags]);
      }
      return Promise.resolve(defaultSubscribedTags);
    }
    return Promise.resolve([]);
  }

  getSubscribedTags() {
    if (this.getDevice().id) {
      const tags = this.storageService.getCache("subscribedTags");
      if (tags) {
        return Promise.resolve(tags);
      } else {
        return this.pushApiClient.getSubscribedTags(this.getDevice()).then((subScribedTags) => {
          this.storageService.addCache("subscribedTags", subScribedTags, 3600);
          return subScribedTags;
        });
      }
    } else {
      throw new Error("Device is not initialized. Unable to retrieve the subscribe tags");
    }
  }

  subscribeTo(tag) {
    if (this.getDevice().id) {
      this.storageService.removeCache("subscribedTags");
      this.storageService.storeItemLocalStorage(this.config.app.localStorage.lastTokenUpdateKey, new Date());
      return this.pushApiClient.subscribe(this.getDevice(), tag).then(() => {
        this.storeHeartbeat();
      });
    } else {
      throw new Error("Device is not initialized. Unable to subscribe to tag");
    }
  }

  unsubscribeFrom(tag) {
    if (this.getDevice().id) {
      this.storageService.removeCache("subscribedTags");
      this.storageService.storeItemLocalStorage(this.config.app.localStorage.lastTokenUpdateKey, new Date());
      return this.pushApiClient.unsubscribe(this.getDevice(), tag);
    } else {
      throw new Error("Device is not initialized. Unable to unsubscribe from tag");
    }
  }

  getArchive() {
    return this.storageService.getAllEntries();
  }

  getTags() {
    const tags = this.storageService.getCache("tags");
    if (tags) {
      return Promise.resolve(tags);
    } else {
      return this.pushApiClient.getTags(this.getDevice()).then((allTags) => {
        this.storageService.addCache("tags", allTags, 3600);
        return allTags;
      });
    }
  }

  resubscribe() {
    this.getSubscribedTags()
      .then((tags) => {
        const tagIds = tags.map((tag) => tag.id);
        this.subscribeTo(tagIds);
      })
      .catch((e) => console.error("Error resubscribing: ", e));
  }

  resubscribeCheck() {
    if (this.storageService.getItemLocalStorage(this.config.app.localStorage.lastTokenUpdateKey)) {
      const lastSubscriptionDate = new Date(this.storageService.getItemLocalStorage(this.config.app.localStorage.lastTokenUpdateKey));
      const now = new Date();
      const diff = now - lastSubscriptionDate;
      if (diff > this.config.app.localStorage.ttl) {
        this.resubscribe();
      }
    } else {
      console.log("should resubscribe");
      this.storageService.storeItemLocalStorage(this.config.app.localStorage.lastTokenUpdateKey, new Date());
      this.resubscribe();
    }
  }

  async storeHeartbeat() {
    const subscribedTags = await this.getSubscribedTags();
    const subscribedTagsIds = subscribedTags.map((tag) => tag.id);
    const beat = {
      deviceId: this.getDevice().id,
      date: new Date(),
      subscribedTags: subscribedTagsIds,
    };
    this.storageService.heartBeat(beat);
  }

  subscribeToTagSourceID(tagSourceID) {
    return new Promise((resolve, reject) => {
      this.fromTagSourceIDToTagID(tagSourceID).then((id) =>
        this.subscribeTo(id).then(() => {
          resolve();
        })
      );
    });
  }

  fromTagSourceIDToTagID(tagSourceID) {
    return new Promise((resolve, reject) => {
      if (typeof tagSourceID === "string") {
        const tags = this.getTags().then((serverTags) => {
          const tagGroup = defaultTagsFilter(serverTags);
          const allTags = [];
          for (const group of tagGroup) {
            allTags.push(...group.tags);
          }
          const tag = allTags.filter((tag) => tag.sourceId === tagSourceID);
          const { id } = tag[0];
          resolve(id);
        });
      } else {
        console.log("tagSourceID not valid");
        reject();
      }
    });
  }

  validateConfig(config) {
    if (!(config.app && config.app.api.id && config.app.api.baseUrl && config.app.firebase)) {
      throw new Error("Push.delivery configuration is not valid!");
    }
  }

  debug() {
    const p = this;
    this.getSubscribedTags().then((subscribedTags) => {
      const text = "PushDeliveryDebug\n-----------------\nDevice:   " + p.getDevice().id + "\n\nSubscribedTags:" + JSON.stringify(subscribedTags);
      p.getTags().then((tags) => {
        let result = text + "\n\nTags:" + JSON.stringify(tags);
        console.log(result);
      });
    });
    return "";
  }
}

window.PushDelivery = PushDelivery;
